import { useEffect, useMemo, useRef, useState } from 'react';
import { MantineReactTable, useMantineReactTable } from 'mantine-react-table';
import axios from '../../../axios';
import { Paper, Button } from '@mantine/core';
import * as XLSX from 'xlsx'; // Importing the xlsx library

const Example = ({ centre, batch, status, milestone, year,generateClicked }) => {
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(value);
  };

  const columns = useMemo(
    () => [
      { accessorKey: 'centre_name', header: 'Centre Name' },
      { accessorKey: 'invoice_no', header: 'Invoice Id' },
      {
        accessorKey: 'created_date',
        header: 'Created Date',
        Cell: ({ cell }) => formatDate(cell.getValue()),
      },
      { accessorKey: 'milestone', header: 'Milestone' },
      {
        accessorKey: 'gross_amount',
        header: 'Gross Amount',
        Cell: ({ cell }) => formatCurrency(cell.getValue()),
      },
      {
        accessorKey: 'tds_amount',
        header: 'TDS Amount',
        Cell: ({ cell }) => formatCurrency(cell.getValue()),
      },
      {
        accessorKey: 'penal_deduction',
        header: 'Penal Deduction',
        Cell: ({ cell }) => formatCurrency(cell.getValue()),
      },
      { accessorKey: 'status', header: 'Status' },
      {
        accessorKey: 'net_payment',
        header: 'Net Payment',
        Cell: ({ cell }) => formatCurrency(cell.getValue()),
      },
      {
        accessorKey: 'book',
        header: 'Book',
        Cell: ({ cell }) => formatCurrency(cell.getValue()),
      },
      { accessorKey: 'kit', header: 'Kit', Cell: ({ cell }) => formatCurrency(cell.getValue()) },
      { accessorKey: 'centre_share', header: 'Centre Share' },
      {
        accessorKey: 'tds',
        header: 'Centre TDS',
        Cell: ({ cell }) => formatCurrency(cell.getValue()),
      },
      {
        accessorKey: 'after_tds',
        header: 'After TDS',
        Cell: ({ cell }) => formatCurrency(cell.getValue()),
      },
      {
        accessorKey: 'received_date',
        header: 'Received Date',
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return value ? formatDate(value) : 'N/A';
        },
      },
    ],
    []
  );

  const rowVirtualizerInstanceRef = useRef(null);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sorting, setSorting] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.post('/invoice/AccountReport', {
          financial_year: year,
          centre_id: centre,
          batch_id: batch,
          status: status,
          milestone: milestone,
        });
        setData(res.data); // Set the fetched data to state
      } catch (err) {
        console.log(err);
      }
    };

    fetchData();
  }, [generateClicked]);

  useEffect(() => {
    try {
      rowVirtualizerInstanceRef.current?.scrollToIndex(0);
    } catch (e) {
      console.log(e);
    }
  }, [sorting]);

  const table = useMantineReactTable({
    data,
    columns,
    enableGlobalFilter: true,
    enableColumnFilterModes: true,
    enablePinning: true,
    enablePagination: true,
    enableMultiSort: true, 
    columnFilterDisplayMode:"popover",
    paginationDisplayMode:"pages",
    positionToolbarAlertBanner:"bottom",
    enableColumnResizing: true,
    enableColumnFilter: true,
    enableColumnSelection: true,
    enableGlobalFilter: true,
    enableGlobalFilterModes: true,
    enableStickyHeader: true,
    enableColumnSticky: true,
    enableRowNumbers: true,
    enableRowActions: true,
  });

  // Mapping for header names in the exported file
  const headerMapping = {
    sr_no:'Sr No',
    centre_name: 'Centre Name',
    invoice_no: 'Invoice Id',
    created_date: 'Created Date',
    milestone: 'Milestone',
    gross_amount: 'Gross Amount',
    tds_amount: 'TDS Amount',
    penal_deduction: 'Penal Deduction',
    status: 'Status',
    net_payment: 'Net Payment',
    book: 'Book',
    kit: 'Kit',
    centre_share: 'Centre Share',
    tds: 'Centre TDS',
    after_tds: 'After TDS',
    received_date: 'Received Date',
  };

  // Function to format date for Excel
  const formatDateForExcel = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB'); // Format as dd/mm/yyyy
  };

  // Function to export data to Excel with modified headers
  const exportToExcel = () => {
    const filteredData = table.getFilteredRowModel().rows.map(({ original }, index) => ({
      sr_no: index + 1,
      centre_name: original.centre_name,
      invoice_no: original.invoice_no,
      created_date: formatDateForExcel(original.created_date), // Format date for Excel
      milestone: original.milestone,
      gross_amount: original.gross_amount,
      tds_amount: original.tds_amount,
      penal_deduction: original.penal_deduction,
      status: original.status,
      net_payment: original.net_payment,
      book: original.book,
      kit: original.kit,
      centre_share: original.centre_share,
      tds: original.tds,
      after_tds: original.after_tds,
      received_date: formatDateForExcel(original.received_date), // Format date for Excel
    }));
  
    if (filteredData.length === 0) {
      alert("No data to export! Please apply a valid filter.");
      return;
    }
  
    const ws = XLSX.utils.json_to_sheet(filteredData);
    ws['!cols'] = Object.keys(headerMapping).map(() => ({ wpx: 120 })); // Set column widths
  
    // Rename headers
    const newHeaders = Object.values(headerMapping);
    newHeaders.forEach((header, index) => {
      ws[XLSX.utils.encode_cell({ r: 0, c: index })].v = header;
    });
  
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Invoices Report');
    XLSX.writeFile(wb, 'Invoices_Report.xlsx');
  };
  
  return (
    <Paper shadow="sm" p="lg" withBorder>
      <Button onClick={exportToExcel} mb="md" color='green'>Export to Excel</Button> {/* Export Button */}
      <div style={{width: '100%' }}>
        <MantineReactTable
          table={table}
          striped
          highlightOnHover
          horizontalSpacing="md"
          verticalSpacing="sm"
          fontSize="sm"
          withColumnBorders
          borderRadius="md"
          withGlobalFilter
          height={400}
          rowVirtualizerInstanceRef={rowVirtualizerInstanceRef}
          enablePagination
        />
      </div>
    </Paper>
  );
};

export default Example;
